// ════════════════════════════════════════════════════════════
// FacturasCobrar — ventas pendientes de facturar y facturas pendientes de cobro
// Mismo esquema que Facturas a pagar
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, Button, Chip, Alert, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  getFacturasCobrar, getLineasVenta, facturarVentaPendiente, marcarFacturado, descargarFacturaPDF,
  proformaDesdeVenta, descargarProformaPDF
} from '../api';
import PropuestaFacturaIntervencion from './PropuestaFacturaIntervencion';

const ESTADOS: any = {
  pendiente_facturar: { label: 'Pendiente de facturar', color: 'warning' },
  pendiente_cobro:    { label: 'Pendiente de cobro',    color: 'info' },
  cobrada:            { label: 'Cobrada',               color: 'success' },
  facturada_fuera:    { label: 'Facturada fuera del CRM', color: 'default' },
};
const eur = (n: number) => (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

export default function FacturasCobrar() {
  const navigate = useNavigate();
  const [datos, setDatos] = useState<any>({ items: [], resumen: {} });
  const [vista, setVista] = useState(0);        // 0 pendientes · 1 cobradas · 2 todas
  const [search, setSearch] = useState('');
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState<any>(null);
  const [lineas, setLineas] = useState<any[]>([]);
  const [procesando, setProcesando] = useState<number | null>(null);
  const [intervencionFacturar, setIntervencionFacturar] = useState<number | null>(null);

  const cargar = () => {
    setCargando(true);
    getFacturasCobrar({ search })
      .then(setDatos).catch(() => setDatos({ items: [], resumen: {} }))
      .finally(() => setCargando(false));
  };
  useEffect(() => {
    const t = setTimeout(cargar, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const r = datos.resumen || {};
  const hoy = new Date().toISOString().slice(0, 10);
  const todas: any[] = datos.items || [];
  const pendientes = todas.filter((x: any) => x.estado === 'pendiente_facturar' || x.estado === 'pendiente_cobro');
  const cobradas = todas.filter((x: any) => x.estado === 'cobrada');
  const lista = vista === 0 ? pendientes : vista === 1 ? cobradas : todas;

  const verDetalle = async (x: any) => {
    // Una intervencion se revisa en su propia propuesta, con horas y recambios
    if ((x.documento || '').startsWith('INT-')) {
      setIntervencionFacturar(parseInt((x.documento || '').replace('INT-', ''), 10));
      return;
    }
    setDetalle(x);
    setLineas([]);
    try { setLineas(await getLineasVenta(x.id)); } catch { setLineas([]); }
  };

  // Las intervenciones se facturan desde su propuesta (horas, recambios y margen)
  const esIntervencion = (x: any) => (x.documento || '').startsWith('INT-');
  const generarProforma = async (x: any) => {
    if (esIntervencion(x)) {
      setIntervencionFacturar(parseInt((x.documento || '').replace('INT-', ''), 10));
      return;
    }
    setProcesando(x.id);
    try {
      const r = await proformaDesdeVenta(x.id);
      if (window.confirm(`✅ Proforma ${r.numero} creada.\n\n¿Descargar el PDF?`)) {
        await descargarProformaPDF(r.id, r.numero);
      }
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al crear la proforma');
    } finally {
      setProcesando(null);
    }
  };
  const abrirFacturacion = (x: any) => {
    if (esIntervencion(x)) {
      setIntervencionFacturar(parseInt((x.documento || '').replace('INT-', ''), 10));
    } else {
      facturar(x);
    }
  };

  const facturar = async (x: any) => {
    if (!window.confirm(
      `Se emitirá una factura para ${x.cliente} por ${eur(x.total)}.\n\n` +
      `Las líneas del albarán se copiarán automáticamente. ¿Continuar?`)) return;
    setProcesando(x.id);
    try {
      const res = await facturarVentaPendiente(x.id);
      setDetalle(null);
      cargar();
      if (window.confirm(`✅ Factura ${res.numero} emitida.\n\n¿Quieres abrirla en Facturas para revisarla?`)) {
        navigate('/facturas');
      }
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al emitir la factura');
    } finally {
      setProcesando(null);
    }
  };

  const marcarSinFactura = async (x: any) => {
    if (!window.confirm('¿Confirmar que esta venta ya se facturó fuera del CRM?')) return;
    try { await marcarFacturado(x.id); setDetalle(null); cargar(); }
    catch { alert('Error al marcar la venta'); }
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #f59e0b' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Pendiente de facturar</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#f59e0b' }}>
                {r.pendientes_facturar || 0} · {eur(r.importe_pendiente_facturar)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #2563eb' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Facturadas pendientes de cobro</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#2563eb' }}>
                {r.n_pendiente_cobro || 0} · {eur(r.pendiente_cobro)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #16a34a' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Cobradas</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#16a34a' }}>
                {r.cobradas || 0} · {eur(r.importe_cobrado)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs value={vista} onChange={(_, v) => setVista(v)} sx={{ mb: 1 }}>
        <Tab label={`Pendientes (${pendientes.length})`} />
        <Tab label={`Cobradas (${cobradas.length})`} />
        <Tab label="Todas" />
      </Tabs>
      <TextField fullWidth size="small" label="Buscar (cliente, nº factura, oferta, albarán)" sx={{ mb: 2 }}
        value={search} onChange={(e) => setSearch(e.target.value)} />

      {cargando ? <CircularProgress /> : lista.length === 0 ? (
        <Alert severity={vista === 0 ? 'success' : 'info'}>
          {vista === 0 ? 'No hay nada pendiente de facturar ni de cobrar.'
            : vista === 1 ? 'Todavía no hay facturas cobradas.' : 'No hay registros.'}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Documento</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{vista === 1 ? 'Cobrada el' : 'Vencimiento'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map((x: any) => {
                const est = ESTADOS[x.estado] || { label: x.estado, color: 'default' };
                const vencida = x.estado === 'pendiente_cobro' && x.proximo_vencimiento && x.proximo_vencimiento < hoy;
                return (
                  <TableRow key={`${x.tipo}-${x.id}`} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{x.cliente}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{x.documento || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {x.tipo === 'venta' ? x.detalle : 'Factura'} · {x.fecha}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{eur(x.total)}</TableCell>
                    <TableCell sx={{ color: vencida ? 'error.main' : 'inherit', fontWeight: vencida ? 700 : 400 }}>
                      {x.estado === 'pendiente_facturar' ? '—'
                        : `${x.fecha_referencia || '—'}${vencida ? ' · vencida' : ''}`}
                    </TableCell>
                    <TableCell><Chip size="small" label={est.label} color={est.color} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {x.tipo === 'venta' && x.estado === 'pendiente_facturar' && (
                        <>
                          <Button size="small" onClick={() => verDetalle(x)}>Ver</Button>
                          <Button size="small" onClick={() => generarProforma(x)}
                            disabled={procesando === x.id}>📄 Proforma</Button>
                          <Button size="small" variant="contained" sx={{ bgcolor: '#4f46e5' }}
                            disabled={procesando === x.id} onClick={() => abrirFacturacion(x)}>
                            {procesando === x.id ? 'Emitiendo...' : 'Facturar'}
                          </Button>
                        </>
                      )}
                      {x.tipo === 'factura' && (
                        <Button size="small" onClick={() => descargarFacturaPDF(x.id, x.documento)}>📄 PDF</Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Los cobros se registran en Tesorería; al completarse, la factura pasa a Cobradas.
      </Typography>

      {/* Detalle de una venta pendiente */}
      <Dialog open={!!detalle} maxWidth="sm" fullWidth
        onClose={(e, rz) => { if (rz !== 'backdropClick' && rz !== 'escapeKeyDown') setDetalle(null); }}>
        <DialogTitle>{detalle?.documento} — {detalle?.cliente}</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {detalle?.detalle} · {detalle?.fecha}
            {detalle?.usuario_nombre ? ` · Registrado por ${detalle.usuario_nombre}` : ''}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Referencia</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Cant.</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineas.map((l: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell>{l.referencia || '—'}</TableCell>
                    <TableCell>{l.descripcion}</TableCell>
                    <TableCell align="right">{l.cantidad}</TableCell>
                    <TableCell align="right">{eur(l.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Typography variant="body2">
              Base {eur(detalle?.base_imponible)} · IVA {eur(detalle?.cuota_iva)} · <b>Total {eur(detalle?.total)}</b>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button color="inherit" onClick={() => marcarSinFactura(detalle)}>Marcar facturada fuera del CRM</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setDetalle(null)}>Cerrar</Button>
          <Button variant="contained" sx={{ bgcolor: '#4f46e5' }} onClick={() => facturar(detalle)}>
            🧾 Emitir factura
          </Button>
        </DialogActions>
      </Dialog>

      <PropuestaFacturaIntervencion open={!!intervencionFacturar}
        intervencionId={intervencionFacturar}
        onClose={() => setIntervencionFacturar(null)}
        onFacturada={cargar} />
    </Box>
  );
}
